import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Shield, TrendingUp, Users, Building2, GraduationCap, Wallet, FileText, ArrowLeft, Check } from "lucide-react";
import Navbar from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useT } from "@/context/I18nContext";

function fmtEur(cents) {
  return `€${((cents || 0) / 100).toFixed(2)}`;
}

function StatCard({ label, value, sub, testid }) {
  return (
    <div data-testid={testid} className="rounded-2xl border border-slate-800 bg-slate-900/60 p-5">
      <div className="text-[10px] uppercase tracking-widest text-slate-400">{label}</div>
      <div className="mt-2 text-3xl font-extrabold text-cyan-300">{value}</div>
      {sub && <div className="mt-1 text-xs text-slate-500">{sub}</div>}
    </div>
  );
}

export default function AdminDashboard() {
  const { user, loading } = useAuth();
  const { locale, formatDate } = useT();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [denied, setDenied] = useState(false);

  useEffect(() => {
    if (loading) return;
    if (!user) { nav("/"); return; }
    api.get("/admin/overview")
      .then(({ data }) => setData(data))
      .catch((err) => {
        if (err?.response?.status === 403) setDenied(true);
        else toast.error(err?.response?.data?.detail || "Errore");
      });
  }, [user, loading, nav]);

  if (denied) {
    return (
      <div className="min-h-screen text-slate-100">
        <Navbar />
        <main className="mx-auto max-w-md px-4 py-24 text-center">
          <Shield className="mx-auto h-16 w-16 text-red-400" />
          <h1 className="mt-4 text-2xl font-bold">Accesso negato</h1>
          <p className="mt-2 text-slate-400">Solo l'amministratore puo accedere a questa pagina.</p>
          <Button onClick={() => nav("/")} className="mt-6 bg-cyan-400 text-slate-950 hover:bg-cyan-300">
            Torna alla home
          </Button>
        </main>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen text-slate-100">
        <Navbar />
        <main className="mx-auto max-w-md px-4 py-24 text-center text-slate-400">Caricamento...</main>
      </div>
    );
  }

  const markPaid = async (id) => {
    try {
      await api.post(`/admin/payouts/${id}/mark-paid`);
      toast.success("Payout segnato come pagato");
      const { data: d } = await api.get("/admin/overview");
      setData(d);
    } catch (err) { toast.error(err?.response?.data?.detail || "Errore"); }
  };

  const closeQuote = async (id) => {
    try {
      await api.post(`/admin/business/${id}/close`);
      toast.success("Richiesta chiusa");
      const { data: d } = await api.get("/admin/overview");
      setData(d);
    } catch (err) { toast.error(err?.response?.data?.detail || "Errore"); }
  };

  return (
    <div className="min-h-screen text-slate-100">
      <Navbar />
      <main className="mx-auto max-w-7xl px-4 py-10 sm:px-6" data-testid="admin-dashboard">
        <button onClick={() => nav("/dashboard")} className="mb-4 flex items-center gap-2 text-sm text-slate-400 hover:text-cyan-300">
          <ArrowLeft className="h-4 w-4" /> Dashboard utente
        </button>
        <div className="mb-6 flex items-center gap-3">
          <div className="grid h-11 w-11 place-items-center rounded-xl bg-gradient-to-br from-amber-400 to-red-500">
            <Shield className="h-6 w-6 text-slate-950" />
          </div>
          <div>
            <h1 className="text-3xl font-extrabold tracking-tight">Admin Console</h1>
            <p className="text-sm text-slate-400">Panoramica vendite, affiliati, B2B e student.</p>
          </div>
        </div>

        {/* KPIs */}
        <section className="grid grid-cols-2 gap-4 md:grid-cols-4">
          <StatCard testid="admin-kpi-today" label="Vendite oggi" value={fmtEur(data.today.cents)} sub={`${data.today.count} ordini · ${data.today.codes} codici`} />
          <StatCard testid="admin-kpi-yesterday" label="Ieri" value={fmtEur(data.yesterday.cents)} sub={`${data.yesterday.count} ordini`} />
          <StatCard testid="admin-kpi-week" label="Ultimi 7 giorni" value={fmtEur(data.week.cents)} sub={`${data.week.count} ordini`} />
          <StatCard testid="admin-kpi-licenses" label="Licenze vendute" value={`${data.licenses_sold}/${data.licenses_max}`} sub={`${data.users_count} utenti registrati`} />
        </section>

        <div className="mt-8 grid gap-6 lg:grid-cols-2">
          {/* Top affiliates */}
          <section className="rounded-2xl border border-slate-800 bg-slate-900/50 p-5">
            <div className="mb-4 flex items-center gap-2">
              <TrendingUp className="h-4 w-4 text-cyan-300" />
              <h2 className="font-semibold">Top affiliati</h2>
              <span className="ml-auto text-xs text-slate-500">{data.top_affiliates.length}</span>
            </div>
            {data.top_affiliates.length === 0 ? (
              <p className="text-sm text-slate-500">Nessun affiliato con vendite ancora.</p>
            ) : (
              <div className="space-y-2">
                {data.top_affiliates.map((a) => (
                  <div key={a.email} data-testid={`top-aff-${a.referral_code}`} className="flex items-center justify-between rounded-lg border border-slate-800 bg-slate-950/60 p-3">
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-sm text-slate-100">{a.email}</div>
                      <div className="mono text-xs text-cyan-300">{a.referral_code}</div>
                    </div>
                    <div className="text-right">
                      <div className="text-sm font-bold text-emerald-300">{fmtEur(a.referral_earnings_cents)}</div>
                      <div className="text-[11px] text-slate-500">{a.referral_paying_count || 0} vendite</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>

          {/* Payout requests */}
          <section className="rounded-2xl border border-slate-800 bg-slate-900/50 p-5">
            <div className="mb-4 flex items-center gap-2">
              <Wallet className="h-4 w-4 text-emerald-300" />
              <h2 className="font-semibold">Richieste payout</h2>
              <span className="ml-auto rounded-full bg-emerald-500/20 px-2 py-0.5 text-xs text-emerald-300">
                {data.payouts_pending.length}
              </span>
            </div>
            {data.payouts_pending.length === 0 ? (
              <p className="text-sm text-slate-500">Nessuna richiesta in attesa.</p>
            ) : (
              <div className="space-y-2">
                {data.payouts_pending.map((p) => (
                  <div key={p.created_at} className="rounded-lg border border-slate-800 bg-slate-950/60 p-3">
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="text-sm">{p.user_email}</div>
                        <div className="text-xs text-slate-500">{p.holder_name}</div>
                        <div className="mono text-[11px] text-slate-400">{p.iban}</div>
                      </div>
                      <div className="text-right">
                        <div className="text-lg font-bold text-emerald-300">{fmtEur(p.amount_cents)}</div>
                        <Button
                          size="sm"
                          onClick={() => markPaid(p._id || p.user_email)}
                          className="mt-2 h-7 bg-emerald-400 text-slate-950 hover:bg-emerald-300"
                        >
                          <Check className="mr-1 h-3 w-3" /> Segna pagato
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>

          {/* B2B queue */}
          <section className="rounded-2xl border border-slate-800 bg-slate-900/50 p-5">
            <div className="mb-4 flex items-center gap-2">
              <Building2 className="h-4 w-4 text-cyan-300" />
              <h2 className="font-semibold">Richieste B2B</h2>
              <span className="ml-auto rounded-full bg-cyan-500/20 px-2 py-0.5 text-xs text-cyan-300">
                {data.b2b_queue.length}
              </span>
            </div>
            {data.b2b_queue.length === 0 ? (
              <p className="text-sm text-slate-500">Nessuna richiesta in coda.</p>
            ) : (
              <div className="space-y-2">
                {data.b2b_queue.map((q) => (
                  <div key={q.created_at + q.email} className="rounded-lg border border-slate-800 bg-slate-950/60 p-3">
                    <div className="flex items-center justify-between">
                      <div className="min-w-0 flex-1">
                        <div className="truncate text-sm font-medium">{q.company_name}</div>
                        <div className="text-xs text-slate-400">{q.email} · {q.vat_number}</div>
                        <div className="mt-1 text-xs">
                          <span className="rounded bg-slate-800 px-2 py-0.5 text-cyan-300 mono">{q.quantity}x</span>
                          <span className="ml-2 text-slate-500">{q.plan_lookup_key}</span>
                        </div>
                        {q.notes && <div className="mt-1 truncate text-[11px] text-slate-500">{q.notes}</div>}
                      </div>
                      <Button
                        size="sm"
                        onClick={() => closeQuote(q._id)}
                        className="ml-2 h-7 bg-slate-800 text-slate-200 hover:bg-slate-700"
                      >
                        Chiudi
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>

          {/* Students pending */}
          <section className="rounded-2xl border border-slate-800 bg-slate-900/50 p-5">
            <div className="mb-4 flex items-center gap-2">
              <GraduationCap className="h-4 w-4 text-emerald-300" />
              <h2 className="font-semibold">Verifiche studenti pending</h2>
              <span className="ml-auto rounded-full bg-emerald-500/20 px-2 py-0.5 text-xs text-emerald-300">
                {data.students_pending.length}
              </span>
            </div>
            {data.students_pending.length === 0 ? (
              <p className="text-sm text-slate-500">Tutte le verifiche completate.</p>
            ) : (
              <div className="space-y-1">
                {data.students_pending.map((s) => (
                  <div key={s.email} className="flex items-center justify-between rounded-lg border border-slate-800 bg-slate-950/60 p-2 px-3 text-sm">
                    <span>{s.email}</span>
                    <span className="text-xs text-slate-500">{formatDate(s.requested_at)}</span>
                  </div>
                ))}
              </div>
            )}
          </section>
        </div>
      </main>
    </div>
  );
}
